import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { createAgentClient } from "../api/agentClient";
import type {
  BoardQueueRow,
  ClinicRoom,
  StaffRequest,
  WalkInIntakeInput,
} from "../api/clinicalWorkspace";
import type { HealthCardPhotosClient } from "../api/healthCardPhotos";
import { type ClinicalRuntime, useClinicalRuntime } from "../auth/ClinicalRuntimeProvider";
import { Icon } from "../components/Icon";
import { PhysicianRosterBadge } from "../components/PhysicianRosterBadge";
import { TodayPatientDetails } from "../components/TodayPatientDetails";
import { TodayRoomPicker } from "../components/TodayRoomPicker";
import { type RoomVisit, TodayRoomsBoard } from "../components/TodayRoomsBoard";
import { TodayStaffRequests } from "../components/TodayStaffRequests";
import { TodayVisitCloser, type VisitCloseOutcome } from "../components/TodayVisitCloser";
import {
  WalkInDialog,
  type WalkInDialogClient,
  type WalkInPhoneCodes,
} from "../components/WalkInDialog";
import { WalkInIntakeCode } from "../components/WalkInIntakeCode";
import { useOptionalDemoScheduling } from "../demo/DemoSchedulingProvider";
import { patientAppOriginConfigured } from "../demo/demoSchedulingClient";
import { useEmrPageImportAccess } from "../emr-import/useEmrPageImport";
import { type PreparedHealthCardPhoto, sendHealthCardPhotoWithin } from "../lib/healthCardPhoto";
import {
  boardRoom,
  boardStatusLabels,
  canStartVisit,
  getBoardGroups,
  getBoardMetrics,
  isActiveBoardRow,
  isScheduled,
  patientDemographics,
} from "../lib/todayBoard";
import { useEncounterSelection } from "../state/encounterSelection";
import { microphoneOwner } from "../state/microphoneOwnership";
import { usePatientProfileTab } from "../state/patientProfileTab";
import {
  useTodayBoardView,
  useTodayRoomsPreferences,
  useTodayStaffPanelOpen,
} from "../state/todayBoardView";
import { BookingReviewDialog } from "./schedule/BookingReviewDialog";
import { IntakeReviewDialog } from "./schedule/IntakeReviewDialog";
import { IntakeReviewPill } from "./schedule/IntakeReviewPill";
import type { IntakeReviewClient } from "./schedule/intakeReviewQueries";
import { ScheduleModal } from "./schedule/ScheduleModal";
import { TodayExpectedAppointments } from "./schedule/TodayExpectedAppointments";
import {
  canSendIntakeToPhone,
  visitIntakeLookup,
  visitIntakeRowState,
  visitIntakeWaiting,
} from "./schedule/visitIntake";
import "../today-board.css";

export type PersistedBoardClient = Pick<
  ReturnType<typeof createAgentClient>,
  | "getTodayBoard"
  | "createWalkIn"
  | "transitionAppointment"
  | "getTodayRooms"
  | "createClinicRoom"
  | "updateClinicRoom"
  | "deleteClinicRoom"
  | "assignAppointmentRoom"
  | "releaseAppointmentRoom"
  | "getStaffRequests"
  | "createStaffRequest"
  | "transitionStaffRequest"
> &
  Partial<
    Pick<
      ReturnType<typeof createAgentClient>,
      | "getPatientWorkspace"
      | "getWorkforceProfile"
      | "listClinicPhysicians"
      | "getTodayBoardMetrics"
      | "searchWalkInPatients"
      | "startPatientEncounter"
      | "getWalkInChartMatches"
      | "downloadPreparedReferralPdf"
      | "getSchedulingIntakeReview"
      | "getSchedulingPatientMatches"
      | "reconcileSchedulingPatient"
      | "applySchedulingIntakeDemographics"
      | "applySchedulingIntakeChartEntries"
      | "applySchedulingIntakeReason"
      | "markSchedulingIntakeSectionReviewed"
      | "getPatientProfile"
      | "getPatientProfileSummary"
      | "updatePatientProfile"
      | "updatePatientDemographics"
      | "listPatientDirectory"
      | "getPatientConsentDocuments"
      | "recordPatientMessagingDecision"
    >
  > &
  Partial<
    Pick<
      HealthCardPhotosClient,
      | "getHealthCardPhotoSettings"
      | "uploadPatientHealthCardPhoto"
      | "listAppointmentHealthCardPhotos"
      | "openHealthCardPhoto"
    >
  >;

/** The decision route and the clinic's notice, when the board client has both. */
function walkInMessagingRoutes(
  client: PersistedBoardClient,
): Pick<WalkInDialogClient, "getPatientConsentDocuments" | "recordPatientMessagingDecision"> {
  const { getPatientConsentDocuments, recordPatientMessagingDecision } = client;
  return getPatientConsentDocuments === undefined || recordPatientMessagingDecision === undefined
    ? {}
    : { getPatientConsentDocuments, recordPatientMessagingDecision };
}

/** The intake review's routes from a board client that has them all, else the runtime's own. */
function intakeReviewClient(
  client: PersistedBoardClient,
  fallback: IntakeReviewClient,
): IntakeReviewClient {
  const {
    getSchedulingIntakeReview,
    getSchedulingPatientMatches,
    reconcileSchedulingPatient,
    applySchedulingIntakeDemographics,
    applySchedulingIntakeChartEntries,
    applySchedulingIntakeReason,
    markSchedulingIntakeSectionReviewed,
    getPatientProfile,
    getPatientProfileSummary,
    updatePatientProfile,
    updatePatientDemographics,
    listPatientDirectory,
    listAppointmentHealthCardPhotos,
    openHealthCardPhoto,
  } = client;
  return getSchedulingIntakeReview &&
    getSchedulingPatientMatches &&
    reconcileSchedulingPatient &&
    applySchedulingIntakeDemographics &&
    applySchedulingIntakeChartEntries &&
    applySchedulingIntakeReason &&
    markSchedulingIntakeSectionReviewed &&
    getPatientProfile &&
    getPatientProfileSummary &&
    updatePatientProfile &&
    updatePatientDemographics
    ? {
        getSchedulingIntakeReview,
        getSchedulingPatientMatches,
        reconcileSchedulingPatient,
        applySchedulingIntakeDemographics,
        applySchedulingIntakeChartEntries,
        applySchedulingIntakeReason,
        markSchedulingIntakeSectionReviewed,
        getPatientProfile,
        getPatientProfileSummary,
        updatePatientProfile,
        updatePatientDemographics,
        ...(listPatientDirectory === undefined ? {} : { listPatientDirectory }),
        // The questionnaire's health card photo (PA-13), when the board client has its routes.
        ...(listAppointmentHealthCardPhotos === undefined || openHealthCardPhoto === undefined
          ? {}
          : { listAppointmentHealthCardPhotos, openHealthCardPhoto }),
      }
    : fallback;
}

const BOARD_REFETCH_INTERVAL_MS = 10_000;

function rowStatus(row: BoardQueueRow) {
  return isScheduled(row) ? "Awaiting check-in" : boardStatusLabels[row.status];
}

function PatientRow({
  row,
  rank,
  selected,
  expanded,
  onSelect,
  onToggleDetails,
  onOpen,
  onCheckIn,
  renderDetails,
  busy,
  intakePill,
}: {
  row: BoardQueueRow;
  rank?: number | undefined;
  selected: boolean;
  expanded: boolean;
  onSelect: () => void;
  onToggleDetails: () => void;
  onOpen: () => void;
  onCheckIn: () => void;
  renderDetails: (id: string) => ReactNode;
  busy: boolean;
  /** The booking's questionnaire pill (Schedule's appointment joined on encounterId). */
  intakePill?: ReactNode;
}) {
  const detailsId = useId();
  return (
    <li className={`tb-patient-row ${selected ? "is-selected" : ""}`}>
      <div className="tb-patient-header">
        <span className="tb-rank" aria-hidden="true">
          {rank ?? "·"}
        </span>
        <button
          type="button"
          className="tb-avatar tb-patient-select"
          aria-label={`Select ${row.displayName}`}
          aria-pressed={selected}
          onClick={onSelect}
        >
          <span aria-hidden="true">{row.initials}</span>
        </button>
        <div className="tb-person">
          <button
            type="button"
            className="tb-patient-name"
            aria-label={`Details for ${row.displayName}`}
            aria-expanded={expanded}
            aria-controls={detailsId}
            onClick={onToggleDetails}
          >
            <span>{row.displayName}</span>
            <Icon name="chevron" />
          </button>
          <PhysicianRosterBadge physician={row.rosterPhysician} />
          <small>
            {patientDemographics(row)} · {boardRoom(row) ?? "Lobby"}
          </small>
          {intakePill}
        </div>
        <span className="tb-reason">{row.reason}</span>
        <span className={`tb-state tb-state-${row.status}`}>{rowStatus(row)}</span>
        <span className="tb-wait">{isScheduled(row) ? "—" : `${row.waitMinutes}m`}</span>
        {isScheduled(row) && row.appointmentId && row.appointmentVersion !== undefined ? (
          <button
            type="button"
            className="tb-button tb-row-action"
            disabled={busy}
            onClick={onCheckIn}
            aria-label={`Check in ${row.displayName}`}
          >
            Check in
          </button>
        ) : (
          <button
            type="button"
            className="tb-button tb-row-action"
            onClick={onOpen}
            aria-label={`Encounter for ${row.displayName}`}
          >
            Encounter
          </button>
        )}
      </div>
      {renderDetails(detailsId)}
    </li>
  );
}

export function TodayPage() {
  const runtime = useClinicalRuntime();
  return <PersistedTodayPage runtime={runtime} />;
}

export function PersistedTodayPage({
  runtime,
  clientOverride,
}: {
  readonly runtime: ClinicalRuntime;
  readonly clientOverride?: PersistedBoardClient;
}) {
  const queryClient = useQueryClient();
  const scheduling = useOptionalDemoScheduling();
  const schedulingDate = new Intl.DateTimeFormat("en-CA", {
    timeZone: scheduling?.state?.configuration.timeZone ?? "America/Toronto",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const schedulingState = scheduling?.state;
  /*
   * Board rows join Schedule's bookings on encounterId (set at check-in), whatever made the
   * booking, and a walk-in's own scheduling row once the desk sent it a code (PA-5), so the
   * questionnaire's pill shows on the patient's row, in the hero card and in the Lobby.
   */
  const intakeFor = useMemo(() => visitIntakeLookup(schedulingState), [schedulingState]);
  const [intakeReviewId, setIntakeReviewId] = useState<string | null>(null);
  // Review booking (PA-10): a returning patient's online booking still without a chart.
  const [bookingReviewId, setBookingReviewId] = useState<string | null>(null);
  /*
   * The desk can send the questionnaire to a patient's phone (PA-5): durable scheduling with the
   * public patient lane on, and a patient app origin a phone can reach.
   */
  const phoneCodes =
    scheduling?.issueVisitIntakeLink !== undefined &&
    schedulingState?.patientLinksEnabled === true &&
    patientAppOriginConfigured();
  const [codeFor, setCodeFor] = useState<{
    readonly encounterId: string;
    readonly displayName: string;
  } | null>(null);
  const hasExpectedBooking =
    scheduling?.state?.appointments.some(
      (appointment) =>
        ["booked", "confirmed"].includes(appointment.status) &&
        !appointment.encounterId &&
        schedulingDate.format(new Date(appointment.startAt)) === schedulingDate.format(new Date()),
    ) ?? false;
  const navigate = useNavigate();
  const { selectEncounter } = useEncounterSelection();
  const { openProfileTab } = usePatientProfileTab();
  const [expandedCards, setExpandedCards] = useState<ReadonlySet<string>>(() => new Set());
  const heroDetailsId = useId();
  const toggleDetails = (cardId: string) => {
    setExpandedCards((current) => {
      const next = new Set(current);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };
  const [view, setView] = useTodayBoardView(runtime.workforce, runtime.config.siteId);
  const [roomsPreferences, setRoomsPreferences] = useTodayRoomsPreferences(
    runtime.workforce,
    runtime.config.siteId,
  );
  const [staffPanelOpen, setStaffPanelOpen] = useTodayStaffPanelOpen(
    runtime.workforce,
    runtime.config.siteId,
  );
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [roomSelectedId, setRoomSelectedId] = useState<string | null>(null);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [walkInSubmitting, setWalkInSubmitting] = useState(false);
  const [walkInError, setWalkInError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [transitionPending, setTransitionPending] = useState(false);
  const transitionLock = useRef(false);
  const walkInLock = useRef(false);
  const walkInIdempotencyKey = useRef(crypto.randomUUID());
  const searchRef = useRef<HTMLInputElement>(null);
  const addWalkInRef = useRef<HTMLButtonElement>(null);
  const nextTabRef = useRef<HTMLButtonElement>(null);
  const roomsTabRef = useRef<HTMLButtonElement>(null);
  const staffToggleRef = useRef<HTMLButtonElement>(null);
  const [roomRevealRequest, setRoomRevealRequest] = useState<{
    roomId: string;
    requestId: number;
  }>();
  const roomRevealSequence = useRef(0);
  const configuredClient = useMemo(
    () =>
      createAgentClient({
        baseUrl: runtime.config.agentApiOrigin,
        getAccessToken: runtime.workforce.accessToken,
        siteId: runtime.config.siteId,
      }),
    [runtime.config.agentApiOrigin, runtime.config.siteId, runtime.workforce.accessToken],
  );
  const client = clientOverride ?? configuredClient;
  const siteId = runtime.config.siteId;
  const { getHealthCardPhotoSettings, uploadPatientHealthCardPhoto } = client;
  const reviewClient = useMemo(
    () => intakeReviewClient(client, configuredClient),
    [client, configuredClient],
  );
  const emrPageImportAllowed = useEmrPageImportAccess().allowed;
  const walkInEmrImport = useMemo(
    () =>
      emrPageImportAllowed
        ? {
            source: "telus-chr" as const,
            client: {
              createWalkIn: client.createWalkIn,
              importEmrPageChart: configuredClient.importEmrPageChart,
              fileMigratedDocument: configuredClient.fileMigratedDocument,
              getPatientProfile: configuredClient.getPatientProfile,
              updatePatientDemographics: configuredClient.updatePatientDemographics,
            },
          }
        : undefined,
    [emrPageImportAllowed, client.createWalkIn, configuredClient],
  );
  const boardQueryKey = useMemo(() => ["today-board", siteId] as const, [siteId]);
  const {
    data: board,
    isPending,
    isError,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: boardQueryKey,
    queryFn: ({ signal }) => client.getTodayBoard(siteId, signal),
    refetchInterval: BOARD_REFETCH_INTERVAL_MS,
  });
  const rows = useMemo(() => (board ?? []).filter(isActiveBoardRow), [board]);
  const metrics = useMemo(() => getBoardMetrics(rows), [rows]);
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return rows.filter((row) =>
      [row.displayName, row.reason, row.room, row.clinician, row.rosterPhysician?.displayName].some(
        (value) => value?.toLocaleLowerCase().includes(query),
      ),
    );
  }, [rows, search]);
  const groups = useMemo(() => getBoardGroups(filtered), [filtered]);
  const defaultFocus =
    groups.ready[0] ??
    groups.inVisit.find((row) => row.assignedToCurrentActor) ??
    groups.inVisit[0] ??
    groups.preparing[0] ??
    groups.wrapUp[0] ??
    groups.scheduled[0];
  const focus = filtered.find((row) => row.encounterId === selectedId) ?? defaultFocus;
  const roomQueryKey = useMemo(() => ["today-rooms", siteId] as const, [siteId]);
  const roomsQuery = useQuery({
    queryKey: roomQueryKey,
    queryFn: ({ signal }) => client.getTodayRooms(siteId, signal),
    refetchInterval: BOARD_REFETCH_INTERVAL_MS,
  });
  const staffQueryKey = useMemo(() => ["staff-requests", siteId] as const, [siteId]);
  const staffQuery = useQuery({
    queryKey: staffQueryKey,
    queryFn: ({ signal }) => client.getStaffRequests(siteId, signal),
    refetchInterval: BOARD_REFETCH_INTERVAL_MS,
  });
  const todayStaffRequests = (staffQuery.data ?? []).filter(
    (request) => request.source !== "attention",
  );
  const activeStaffCount = todayStaffRequests.filter(
    (request) => request.status === "open" || request.status === "in_progress",
  ).length;
  const staffCountDescription = staffQuery.isError
    ? "Staff requests unavailable"
    : staffQuery.isPending
      ? "Loading staff requests"
      : `${activeStaffCount} open ${activeStaffCount === 1 ? "request" : "requests"}`;
  const roomForStaffRequest = (request: StaffRequest) =>
    roomsQuery.data?.find(
      (room) =>
        room.state === "occupied" &&
        (room.encounterId === request.encounterId ||
          (!!request.appointmentId && room.appointmentId === request.appointmentId)),
    );
  const profileQuery = useQuery({
    queryKey: ["today-clinic-profile", siteId],
    queryFn: () => {
      if (!client.getWorkforceProfile) throw new Error("Clinic profile is unavailable");
      return client.getWorkforceProfile();
    },
    enabled: client.getWorkforceProfile !== undefined,
  });
  const physiciansQuery = useQuery({
    queryKey: ["clinic-physicians", siteId],
    queryFn: ({ signal }) => {
      if (!client.listClinicPhysicians) throw new Error("Clinic physicians are unavailable");
      return client.listClinicPhysicians(siteId, signal);
    },
    enabled: client.listClinicPhysicians !== undefined,
    retry: false,
  });
  const defaultRoomsPreferences = {
    scope: roomsQuery.data?.some(
      (room) =>
        room.zonePhysician?.userId === profileQuery.data?.userId &&
        room.zonePhysician !== undefined,
    )
      ? ("mine" as const)
      : ("all" as const),
    zoneFilter: "",
    layout: "tiles" as const,
  };
  const metricsQueryKey = useMemo(() => ["today-board-metrics", siteId] as const, [siteId]);
  const metricsQuery = useQuery({
    queryKey: metricsQueryKey,
    queryFn: () => {
      if (!client.getTodayBoardMetrics) throw new Error("Clinic metrics are unavailable");
      return client.getTodayBoardMetrics(siteId);
    },
    enabled: client.getTodayBoardMetrics !== undefined,
    refetchInterval: BOARD_REFETCH_INTERVAL_MS,
  });
  const displayedMetrics = client.getTodayBoardMetrics
    ? metricsQuery.isError
      ? undefined
      : metricsQuery.data
    : board
      ? metrics
      : undefined;
  const refreshBoardAndRooms = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: boardQueryKey }),
      queryClient.invalidateQueries({ queryKey: roomQueryKey }),
      queryClient.invalidateQueries({ queryKey: metricsQueryKey }),
      queryClient.invalidateQueries({ queryKey: staffQueryKey }),
    ]);
  };
  const writeBlocked = transitionPending || isPending || isError;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.ctrlKey || event.metaKey || event.altKey || walkInOpen) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest("input, textarea, select, [contenteditable]")
      )
        return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [walkInOpen]);

  const closeWalkIn = () => {
    if (walkInLock.current) return;
    setWalkInOpen(false);
    addWalkInRef.current?.focus();
  };
  const openEncounter = (
    row: Pick<BoardQueueRow, "id" | "encounterId"> & Partial<Pick<BoardQueueRow, "displayName">>,
  ) => {
    if (
      selectEncounter({
        patientId: row.id,
        encounterId: row.encounterId,
        ...(row.displayName ? { label: row.displayName } : {}),
      })
    ) {
      void navigate({ to: "/encounter" });
    }
  };
  const reviewChart = (row: BoardQueueRow) => {
    openProfileTab(row.id, { label: row.displayName, section: "overview" });
    void navigate({
      to: "/patients/$patientId",
      params: { patientId: row.id },
      search: { section: "overview" },
    });
  };
  const submitWalkIn = async (input: WalkInIntakeInput, photo?: PreparedHealthCardPhoto) => {
    if (walkInLock.current) return;
    walkInLock.current = true;
    setWalkInSubmitting(true);
    setWalkInError(null);
    try {
      const result = await client.createWalkIn({
        siteId,
        idempotencyKey: walkInIdempotencyKey.current,
        input,
      });
      walkInIdempotencyKey.current = crypto.randomUUID();
      // The card photo goes once the chart exists: a bounded wait, cancelled at the deadline.
      // A failure never undoes the visit (the chart's Details offer Add photo).
      const photoOutcome =
        photo !== undefined && uploadPatientHealthCardPhoto !== undefined
          ? await sendHealthCardPhotoWithin((signal) =>
              uploadPatientHealthCardPhoto(result.patient.id, photo.bytes, signal),
            )
          : null;
      setWalkInOpen(false);
      setSearch("");
      setSelectedId(result.patient.encounterId);
      setConfirmation(
        `${result.patient.displayName} is ${result.created ? "now waiting in" : "already on"} the persisted queue.${
          photoOutcome === "not_saved"
            ? " The health card photo could not be confirmed; check the chart's Details and add it if needed."
            : ""
        }`,
      );
      addWalkInRef.current?.focus();
      await refreshBoardAndRooms();
    } catch (error) {
      setWalkInError(
        error instanceof Error ? error.message : "Walk-in registration could not be recorded",
      );
    } finally {
      walkInLock.current = false;
      setWalkInSubmitting(false);
    }
  };
  /** The quick walk-in path: the visit is on Today and the dialog stays open on its code. */
  const walkInPhoneCodes: WalkInPhoneCodes = {
    onVisitAdded: (result) => {
      setSearch("");
      setSelectedId(result.patient.encounterId);
      setConfirmation(
        `${result.patient.displayName} ${result.created ? "has been added to Today." : "already has an open visit; their existing visit was kept."}`,
      );
      void refreshBoardAndRooms();
    },
    onReviewIntake: (schedulingId) => {
      setWalkInOpen(false);
      setIntakeReviewId(schedulingId);
    },
  };
  const transition = async (
    row: RoomVisit,
    targetStatus:
      | "waiting"
      | "in_progress"
      | "ready_for_discharge"
      | "completed"
      | VisitCloseOutcome,
  ) => {
    if (
      transitionLock.current ||
      writeBlocked ||
      !row.appointmentId ||
      row.appointmentVersion === undefined
    )
      return;
    const status = row.appointmentStatus ?? "";
    const arrived = ["waiting", "roomed", "in_progress", "ready_for_discharge"].includes(status);
    const allowed =
      targetStatus === "waiting"
        ? status === "scheduled"
        : targetStatus === "in_progress"
          ? status === "roomed" && boardRoom(row) !== undefined
          : targetStatus === "ready_for_discharge"
            ? status === "in_progress"
            : targetStatus === "cancelled"
              ? arrived || status === "scheduled"
              : arrived;
    if (!allowed) return;
    const recording = microphoneOwner();
    if (targetStatus === "in_progress" && recording && recording.encounterId !== row.encounterId) {
      setTransitionError(
        "Finish the current microphone session and wait for transcription before starting another encounter. This patient's visit has not been changed.",
      );
      return;
    }
    const closing = targetStatus === "left" || targetStatus === "cancelled";
    if (closing && recording && recording.encounterId === row.encounterId) {
      setTransitionError(
        "Finish this patient's microphone session and wait for transcription before closing their visit. This patient's visit has not been changed.",
      );
      return;
    }
    if (targetStatus === "completed" && row.encounterSigned === false) {
      setTransitionError(
        "Sign the encounter note first. Signing marks the visit done and sends the room to Needs cleaning. Use Return to lobby if the visit needs to stay open.",
      );
      return;
    }
    transitionLock.current = true;
    setTransitionPending(true);
    setTransitionError(null);
    setConfirmation(null);
    try {
      const receipt = await client.transitionAppointment(row.appointmentId, {
        siteId,
        expectedVersion: row.appointmentVersion,
        targetStatus,
      });
      const room = boardRoom(row);
      const roomNote = room ? ` ${room} needs cleaning.` : "";
      setConfirmation(
        targetStatus === "left"
          ? `${receipt.displayName} left; their visit is closed and no longer on Today.${roomNote}`
          : targetStatus === "cancelled"
            ? `${receipt.displayName}’s visit is cancelled and no longer on Today.${roomNote}`
            : `${receipt.displayName} ${targetStatus === "waiting" ? "is checked in and waiting." : targetStatus === "in_progress" ? "has started their encounter." : targetStatus === "completed" ? "is done. Their room needs cleaning; any unfinished staff requests remain in Needs staff." : "is ready for wrap-up."}`,
      );
      await refreshBoardAndRooms();
      if (targetStatus === "in_progress") openEncounter(receipt);
    } catch (error) {
      setTransitionError(
        error instanceof Error ? error.message : "The appointment could not be updated",
      );
      await refreshBoardAndRooms();
    } finally {
      transitionLock.current = false;
      setTransitionPending(false);
    }
  };
  const assignRoom = async (row: BoardQueueRow, room: ClinicRoom): Promise<boolean> => {
    if (
      transitionLock.current ||
      writeBlocked ||
      roomsQuery.isError ||
      room.state !== "available" ||
      !row.appointmentId ||
      row.appointmentVersion === undefined ||
      !["waiting", "roomed", "in_progress", "ready_for_discharge"].includes(
        row.appointmentStatus ?? "",
      )
    )
      return false;
    transitionLock.current = true;
    setTransitionPending(true);
    setTransitionError(null);
    setConfirmation(null);
    try {
      await client.assignAppointmentRoom(row.appointmentId, {
        siteId,
        expectedVersion: row.appointmentVersion,
        roomId: room.id,
        expectedRoomVersion: room.version,
      });
      setConfirmation(`${row.displayName} is assigned to ${room.name}.`);
      await refreshBoardAndRooms();
      return true;
    } catch (error) {
      setTransitionError(
        error instanceof Error ? error.message : "The room could not be assigned.",
      );
      await refreshBoardAndRooms();
      return false;
    } finally {
      transitionLock.current = false;
      setTransitionPending(false);
    }
  };
  const roomAction = (row: BoardQueueRow) => (
    <TodayRoomPicker
      key={row.encounterId}
      patientName={row.displayName}
      label={boardRoom(row) ? "Move room" : "Assign room"}
      rooms={roomsQuery.data ?? []}
      disabled={
        writeBlocked ||
        roomsQuery.isPending ||
        roomsQuery.isError ||
        !row.appointmentId ||
        row.appointmentVersion === undefined ||
        !["waiting", "roomed", "in_progress", "ready_for_discharge"].includes(
          row.appointmentStatus ?? "",
        )
      }
      onAssign={(room) => assignRoom(row, room)}
    />
  );
  const focusLabel =
    focus?.status === "with-clinician"
      ? "Visit in progress"
      : focus?.status === "ready" && focus.encounterId === groups.ready[0]?.encounterId
        ? "Next up"
        : "Patient focus";
  const focusAction =
    focus?.status === "with-clinician"
      ? "Resume encounter"
      : focus?.status === "ready" && canStartVisit(focus)
        ? "Start encounter"
        : "Encounter";
  /** Send intake to phone, or Show code while one is out (PA-5); never once the answers are in. */
  const intakeAction = (row: BoardQueueRow) => {
    if (!phoneCodes) return null;
    const intake = intakeFor(row.encounterId);
    if (!canSendIntakeToPhone(row, intake)) return null;
    return (
      <button
        type="button"
        className="tb-button"
        onClick={() => setCodeFor({ encounterId: row.encounterId, displayName: row.displayName })}
      >
        {visitIntakeWaiting(intake, new Date()) ? "Show code" : "Send intake to phone"}
      </button>
    );
  };
  const renderDetails = (row: BoardQueueRow, id: string, cardId: string) => (
    <TodayPatientDetails
      id={id}
      expanded={expandedCards.has(cardId)}
      siteId={siteId}
      row={row}
      client={client}
      onReview={() => reviewChart(row)}
      roomAction={roomAction(row)}
      intakeAction={intakeAction(row)}
      closeAction={
        <TodayVisitCloser
          row={row}
          disabled={writeBlocked}
          onClose={(outcome) => void transition(row, outcome)}
        />
      }
      disabled={writeBlocked}
      onComplete={() => void transition(row, "completed")}
    />
  );
  /**
   * The arrived patient's one pill (lead decision S2): the questionnaire's review state, opening
   * the review in place, or "Waiting for answers" while a phone code is out, opening the code.
   */
  const rowIntakePill = (row: BoardQueueRow) => {
    const intake = intakeFor(row.encounterId);
    const pill = visitIntakeRowState(intake, new Date())?.pill ?? null;
    if (pill === null) return null;
    const schedulingId = intake.schedulingId;
    // A booking pill never shows after arrival (check-in needs the chart); it would open Review
    // booking, as on Expected today. Nor does a message pill (texts and emails are a booking's
    // before arrival, PA-7): it has nothing to open here.
    const onOpen =
      pill.kind === "message"
        ? undefined
        : pill.kind === "code"
          ? phoneCodes
            ? () => setCodeFor({ encounterId: row.encounterId, displayName: row.displayName })
            : undefined
          : schedulingId === undefined
            ? undefined
            : pill.kind === "booking"
              ? () => setBookingReviewId(schedulingId)
              : () => setIntakeReviewId(schedulingId);
    return (
      <IntakeReviewPill pill={pill} patientName={row.displayName} surface="today" onOpen={onOpen} />
    );
  };
  const focusIntake = focus ? intakeFor(focus.encounterId) : undefined;
  const focusReviewId =
    focusIntake?.facts?.intakeReview === undefined ? undefined : focusIntake.schedulingId;
  const renderRow = (row: BoardQueueRow, rank?: number) => (
    <PatientRow
      key={row.encounterId}
      row={row}
      rank={rank}
      selected={row.encounterId === focus?.encounterId}
      expanded={expandedCards.has(`row:${row.encounterId}`)}
      onToggleDetails={() => toggleDetails(`row:${row.encounterId}`)}
      renderDetails={(id) => renderDetails(row, id, `row:${row.encounterId}`)}
      onSelect={() => setSelectedId(row.encounterId)}
      onOpen={() => openEncounter(row)}
      onCheckIn={() => void transition(row, "waiting")}
      busy={writeBlocked}
      intakePill={rowIntakePill(row)}
    />
  );
  const sections = [
    {
      title: "Ready for clinician",
      hint: "Ready patients, longest wait first",
      rows: groups.ready,
      ranked: true,
    },
    {
      title: "Not ready yet",
      hint: "Waiting or rooming · readiness follows the recorded visit status",
      rows: groups.preparing,
    },
    { title: "With clinician", hint: "Visits in progress", rows: groups.inVisit },
    { title: "Wrap-up", hint: "Finishing the visit", rows: groups.wrapUp },
  ];
  const liveLabel = isPending
    ? "Loading"
    : isError || roomsQuery.isError || metricsQuery.isError
      ? "Update failed"
      : isFetching
        ? "Refreshing"
        : "Live";

  // Client methods close over their own configuration, so the optional
  // download can be captured once for the callback below.
  const downloadReferralPdf = client.downloadPreparedReferralPdf;
  const staffPanel = (
    <TodayStaffRequests
      requests={todayStaffRequests}
      loading={staffQuery.isPending}
      error={staffQuery.isError}
      disabled={writeBlocked}
      onRetry={() => void staffQuery.refetch()}
      onOpen={openEncounter}
      onOpenWorkItem={(request) => {
        // The holder works the item in Attention, which shows it to them.
        if (request.resultId !== undefined)
          void navigate({ to: "/attention", search: { result: request.resultId } });
        else if (request.reportId !== undefined)
          void navigate({ to: "/attention", search: { report: request.reportId } });
      }}
      onOpenReferralPdf={
        downloadReferralPdf === undefined
          ? undefined
          : async (request) => {
              if (request.referralId === undefined) return;
              const blob = await downloadReferralPdf(
                request.referralId,
                undefined,
                request.referralArtifactId,
              );
              const url = URL.createObjectURL(blob);
              window.open(url, "_blank", "noopener,noreferrer");
              window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
            }
      }
      canShowRoom={(request) => !roomsQuery.isError && !!roomForStaffRequest(request)}
      onShowRoom={(request) => {
        const room = roomForStaffRequest(request);
        if (!room || roomsQuery.isError) return;
        setSearch("");
        setRoomsPreferences({
          ...(roomsPreferences ?? defaultRoomsPreferences),
          scope: "all",
          zoneFilter: "",
        });
        setView("rooms");
        setStaffPanelOpen(false);
        setRoomRevealRequest({ roomId: room.id, requestId: ++roomRevealSequence.current });
      }}
      onTransition={async (request: StaffRequest, targetStatus) => {
        try {
          await client.transitionStaffRequest(request.id, {
            siteId,
            expectedVersion: request.version,
            targetStatus,
          });
        } finally {
          await queryClient.invalidateQueries({ queryKey: staffQueryKey });
        }
      }}
    />
  );

  return (
    <main className="page today-page tb-board">
      <header className="tb-header">
        <div className="tb-heading">
          <h1>{profileQuery.data?.siteName ?? "Today’s clinic"}</h1>
          <span>
            {new Intl.DateTimeFormat(undefined, {
              ...(profileQuery.data?.siteTimezone
                ? { timeZone: profileQuery.data.siteTimezone }
                : {}),
              weekday: "long",
              month: "short",
              day: "numeric",
            }).format(new Date())}
          </span>
        </div>
        <div className="tb-view-controls">
          <div
            className="tb-tabs"
            role="tablist"
            aria-label="Clinic board view"
            onKeyDown={(event) => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
              event.preventDefault();
              const views = ["next", "rooms"] as const;
              const index = views.indexOf(view);
              const next =
                views[
                  event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? views.length - 1
                      : (index + (event.key === "ArrowRight" ? 1 : -1) + views.length) %
                        views.length
                ] ?? "next";
              setView(next);
              ({ next: nextTabRef, rooms: roomsTabRef })[next].current?.focus();
            }}
          >
            <button
              type="button"
              ref={nextTabRef}
              id="tb-next-tab"
              role="tab"
              aria-selected={view === "next"}
              aria-controls="tb-next-panel"
              tabIndex={view === "next" ? 0 : -1}
              onClick={() => setView("next")}
            >
              Who’s next
            </button>
            <button
              type="button"
              ref={roomsTabRef}
              id="tb-rooms-tab"
              role="tab"
              aria-selected={view === "rooms"}
              aria-controls="tb-rooms-panel"
              tabIndex={view === "rooms" ? 0 : -1}
              onClick={() => setView("rooms")}
            >
              Rooms
            </button>
          </div>
          <button
            type="button"
            ref={staffToggleRef}
            id="tb-staff-toggle"
            className="tb-staff-toggle"
            aria-label="Needs staff"
            aria-describedby="tb-staff-count-description"
            aria-expanded={view === "rooms" && staffPanelOpen}
            aria-controls="tb-staff-panel"
            onClick={() => {
              if (view === "rooms" && staffPanelOpen) setStaffPanelOpen(false);
              else {
                setView("rooms");
                setStaffPanelOpen(true);
              }
            }}
          >
            Needs staff{" "}
            <span className="tb-tab-count" aria-hidden="true">
              {staffQuery.isError ? "!" : staffQuery.isPending ? "—" : activeStaffCount}
            </span>
          </button>
          <span className="sr-only" id="tb-staff-count-description">
            {staffCountDescription}
          </span>
        </div>
        <dl className="tb-metrics" aria-label="Clinic metrics">
          <div>
            <dt>in clinic</dt>
            <dd>{displayedMetrics?.inClinic ?? "—"}</dd>
          </div>
          <div title="Median time in clinic for patients waiting, rooming, or ready">
            <dt>median wait</dt>
            <dd>{displayedMetrics ? `${displayedMetrics.medianWait}m` : "—"}</dd>
          </div>
          <div title="Longest time in clinic for patients waiting, rooming, or ready">
            <dt>longest wait</dt>
            <dd>{displayedMetrics ? `${displayedMetrics.longestWait}m` : "—"}</dd>
          </div>
          <div>
            <dt>rooms open</dt>
            <dd>
              {roomsQuery.data && !roomsQuery.isError
                ? roomsQuery.data.filter((room) => room.state === "available").length
                : "—"}
            </dd>
          </div>
          <div>
            <dt>seen today</dt>
            <dd>{metricsQuery.isError ? "—" : (metricsQuery.data?.completedCount ?? "—")}</dd>
          </div>
        </dl>
        <div className="tb-header-actions">
          <label className="tb-search">
            <Icon name="search" />
            <span className="sr-only">Find patient</span>
            <input
              ref={searchRef}
              type="search"
              placeholder="Find patient"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setSearch("");
              }}
            />
            <span className="tb-search-shortcut" aria-hidden="true">
              /
            </span>
          </label>
          <span
            className={`tb-live ${liveLabel === "Update failed" ? "is-error" : ""}`}
            title={
              dataUpdatedAt
                ? `Last updated ${new Date(dataUpdatedAt).toLocaleTimeString()}. Refreshes every 10 seconds.`
                : "Loading clinic queue"
            }
          >
            <span aria-hidden="true" />
            {liveLabel}
          </span>
          <button
            type="button"
            className="tb-button tb-primary"
            ref={addWalkInRef}
            aria-expanded={walkInOpen}
            onClick={() => {
              setConfirmation(null);
              setWalkInError(null);
              setWalkInOpen(true);
            }}
          >
            <Icon name="plus" />
            Add walk-in
          </button>
        </div>
      </header>
      {walkInOpen ? (
        <WalkInDialog
          client={{
            searchWalkInPatients:
              client.searchWalkInPatients ?? configuredClient.searchWalkInPatients,
            startPatientEncounter:
              client.startPatientEncounter ?? configuredClient.startPatientEncounter,
            createWalkIn: client.createWalkIn,
            getWalkInChartMatches:
              client.getWalkInChartMatches ?? configuredClient.getWalkInChartMatches,
            // Recording a walk-in's answer about texts and emails (PA-8).
            ...walkInMessagingRoutes(clientOverride ?? configuredClient),
          }}
          siteId={siteId}
          codes={phoneCodes ? walkInPhoneCodes : undefined}
          onAdded={(result) => {
            setWalkInOpen(false);
            setSearch("");
            setSelectedId(result.patient.encounterId);
            setConfirmation(
              `${result.patient.displayName} ${result.created ? "has been added to Today." : "already has an open visit; their existing visit was kept."}`,
            );
            addWalkInRef.current?.focus();
            void refreshBoardAndRooms();
          }}
          submitting={walkInSubmitting}
          error={walkInError}
          onSubmit={(input, photo) => void submitWalkIn(input, photo)}
          onClose={closeWalkIn}
          healthCardPhotos={
            getHealthCardPhotoSettings !== undefined && uploadPatientHealthCardPhoto !== undefined
              ? { client: { getHealthCardPhotoSettings }, siteId }
              : undefined
          }
          {...(walkInEmrImport === undefined ? {} : { emrImport: walkInEmrImport })}
        />
      ) : null}
      {confirmation ? (
        <p className="sr-only" role="status">
          {confirmation}
        </p>
      ) : null}
      {transitionError ? (
        <p className="tb-notice tb-notice-error" role="alert">
          {transitionError}
        </p>
      ) : null}
      {isError ? (
        <div className="tb-notice tb-notice-error" role="alert">
          <strong>
            {board ? "The queue could not be refreshed" : "The persisted queue could not be loaded"}
          </strong>
          <span>
            {board
              ? "Showing the last received queue. Refresh before updating a visit."
              : "Retry to load today’s clinic queue."}
          </span>
          <button type="button" className="tb-button" onClick={() => void refetch()}>
            Retry loading the board
          </button>
        </div>
      ) : null}
      {isPending ? (
        <div className="tb-empty" role="status">
          <Icon name="clock" />
          <h2>Loading clinic queue</h2>
        </div>
      ) : null}
      {view === "next" && !isPending && board && rows.length === 0 && !hasExpectedBooking ? (
        <div className="tb-empty">
          <Icon name="check" />
          <h2>No patients on the board</h2>
          <p>New arrivals will appear here. Add a walk-in to get started.</p>
        </div>
      ) : null}
      {view === "next" && !isPending && rows.length > 0 && filtered.length === 0 ? (
        <div className="tb-empty" role="status">
          <Icon name="search" />
          <h2>No matching patients</h2>
          <p>Try a patient name, reason for visit, room, or clinician.</p>
          <button type="button" className="tb-button" onClick={() => setSearch("")}>
            Clear search
          </button>
        </div>
      ) : null}
      <section
        id="tb-next-panel"
        role="tabpanel"
        aria-labelledby="tb-next-tab"
        hidden={view !== "next"}
      >
        <TodayExpectedAppointments
          search={search}
          legacyCount={groups.scheduled.length}
          onReviewIntake={setIntakeReviewId}
          onReviewBooking={setBookingReviewId}
        >
          {groups.scheduled.map((row) => renderRow(row))}
        </TodayExpectedAppointments>
        {filtered.length > 0 && focus ? (
          <div className="tb-next-layout">
            <div className="tb-queue">
              <section className="tb-hero" aria-label="Focused patient">
                <div className="tb-hero-header">
                  <span className="tb-avatar tb-avatar-large" aria-hidden="true">
                    {focus.initials}
                  </span>
                  <div className="tb-hero-copy">
                    <p className="tb-eyebrow">{focusLabel}</p>
                    <h2 aria-label={focus.displayName}>
                      <button
                        type="button"
                        className="tb-patient-name"
                        aria-label={`Details for ${focus.displayName}`}
                        aria-expanded={expandedCards.has(`hero:${focus.encounterId}`)}
                        aria-controls={heroDetailsId}
                        onClick={() => toggleDetails(`hero:${focus.encounterId}`)}
                      >
                        <span>{focus.displayName}</span>
                        <Icon name="chevron" />
                      </button>
                    </h2>
                    <PhysicianRosterBadge physician={focus.rosterPhysician} />
                    <p>
                      {patientDemographics(focus)} · {boardRoom(focus) ?? "Lobby"} ·{" "}
                      {isScheduled(focus) ? "Awaiting check-in" : `${focus.waitMinutes}m in clinic`}
                    </p>
                  </div>
                  <div className="tb-hero-actions">
                    <button type="button" className="tb-button" onClick={() => reviewChart(focus)}>
                      Review chart
                    </button>
                    {focusIntake !== undefined && focusReviewId !== undefined ? (
                      <button
                        type="button"
                        className="tb-button"
                        onClick={() => setIntakeReviewId(focusReviewId)}
                      >
                        {visitIntakeRowState(focusIntake, new Date())?.action?.kind ===
                        "review_intake"
                          ? "Review intake"
                          : "Open intake"}
                      </button>
                    ) : null}
                    {roomAction(focus)}
                    {focus.appointmentStatus === "ready_for_discharge" ? (
                      <button
                        type="button"
                        className="tb-button"
                        disabled={writeBlocked}
                        onClick={() => void transition(focus, "completed")}
                        title="A signed encounter note is required; signing marks the visit done automatically"
                      >
                        Mark done
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="tb-button tb-primary"
                      disabled={focusAction === "Start encounter" && writeBlocked}
                      onClick={() =>
                        focusAction === "Start encounter"
                          ? void transition(focus, "in_progress")
                          : openEncounter(focus)
                      }
                    >
                      {transitionPending && focusAction === "Start encounter"
                        ? "Starting encounter…"
                        : focusAction}
                      <Icon name="arrow" />
                    </button>
                    <TodayVisitCloser
                      row={focus}
                      disabled={writeBlocked}
                      onClose={(outcome) => void transition(focus, outcome)}
                    />
                  </div>
                </div>
                {renderDetails(focus, heroDetailsId, `hero:${focus.encounterId}`)}
              </section>
              {sections
                .filter((section) => section.rows.length > 0)
                .map((section) => (
                  <section
                    className="tb-queue-section"
                    key={section.title}
                    aria-label={section.title}
                  >
                    <header>
                      <h2 className="tb-eyebrow">
                        {section.title} <span>{section.rows.length}</span>
                      </h2>
                      <p>{section.hint}</p>
                    </header>
                    <ul className="tb-patients">
                      {section.rows.map((row, index) =>
                        renderRow(row, section.ranked ? index + 1 : undefined),
                      )}
                    </ul>
                  </section>
                ))}
            </div>
          </div>
        ) : null}
      </section>
      <section
        id="tb-rooms-panel"
        role="tabpanel"
        aria-labelledby="tb-rooms-tab"
        hidden={view !== "rooms"}
      >
        {roomsQuery.isPending ? (
          <div className="tb-empty" role="status">
            Loading rooms…
          </div>
        ) : null}
        {roomsQuery.isError ? (
          <div className="tb-notice tb-notice-error" role="alert">
            <span>Rooms could not be refreshed. Reload before changing a room.</span>
            <button type="button" className="tb-button" onClick={() => void roomsQuery.refetch()}>
              Retry rooms
            </button>
          </div>
        ) : null}
        {roomsQuery.data || staffPanelOpen ? (
          <TodayRoomsBoard
            key={siteId}
            {...(profileQuery.data ? { actorUserId: profileQuery.data.userId } : {})}
            physicians={physiciansQuery.data?.physicians ?? []}
            physiciansLoading={physiciansQuery.isFetching}
            physiciansError={
              physiciansQuery.isError
                ? "Clinic physicians could not be loaded."
                : client.listClinicPhysicians
                  ? ""
                  : "Clinic physicians are unavailable."
            }
            onRetryPhysicians={() => void physiciansQuery.refetch()}
            preferences={roomsPreferences ?? defaultRoomsPreferences}
            onPreferencesChange={setRoomsPreferences}
            siteId={siteId}
            roomRevealRequest={roomRevealRequest}
            staffPanel={staffPanel}
            staffPanelOpen={staffPanelOpen}
            onCloseStaffPanel={(restoreFocus) => {
              setStaffPanelOpen(false);
              if (restoreFocus) staffToggleRef.current?.focus();
            }}
            rooms={roomsQuery.data ?? []}
            rows={rows}
            search={search}
            onClearSearch={() => setSearch("")}
            selectedId={roomSelectedId ?? undefined}
            client={client}
            disabled={writeBlocked || roomsQuery.isError}
            onChanged={refreshBoardAndRooms}
            onSelect={(id) => setRoomSelectedId(id || null)}
            onOpen={openEncounter}
            onStart={(row) => void transition(row, "in_progress")}
            onWrap={(row) => void transition(row, "ready_for_discharge")}
            onComplete={(row) => void transition(row, "completed")}
            onClose={(row, outcome) => void transition(row, outcome)}
            lobbyRowExtra={rowIntakePill}
            staffRequests={todayStaffRequests}
            onCreateStaffRequest={async (patient, draft) => {
              await client.createStaffRequest(patient.encounterId, {
                siteId,
                patientId: patient.patientId,
                ...draft,
              });
              await queryClient.invalidateQueries({ queryKey: staffQueryKey });
            }}
          />
        ) : null}
      </section>
      {codeFor !== null && scheduling !== null ? (
        <ScheduleModal
          title="Intake code"
          className="tb-walk-in-modal"
          description={`For ${codeFor.displayName}`}
          onClose={() => setCodeFor(null)}
        >
          <WalkInIntakeCode
            key={codeFor.encounterId}
            encounterId={codeFor.encounterId}
            patientName={codeFor.displayName}
            onDone={() => setCodeFor(null)}
            onReviewIntake={(schedulingId) => {
              setCodeFor(null);
              setIntakeReviewId(schedulingId);
            }}
          />
        </ScheduleModal>
      ) : null}
      {bookingReviewId !== null && scheduling !== null ? (
        <BookingReviewDialog
          key={bookingReviewId}
          appointmentId={bookingReviewId}
          client={reviewClient}
          siteId={siteId}
          onClose={() => setBookingReviewId(null)}
          onReviewIntake={(appointmentId) => {
            setBookingReviewId(null);
            setIntakeReviewId(appointmentId);
          }}
          onOpenChart={(patientId, displayName) => {
            setBookingReviewId(null);
            openProfileTab(patientId, { label: displayName, section: "overview" });
            void navigate({
              to: "/patients/$patientId",
              params: { patientId },
              search: { section: "overview" },
            });
          }}
        />
      ) : null}
      {intakeReviewId !== null && scheduling !== null ? (
        <IntakeReviewDialog
          key={intakeReviewId}
          appointmentId={intakeReviewId}
          client={reviewClient}
          siteId={siteId}
          onClose={() => setIntakeReviewId(null)}
          onOpenChart={(patientId, displayName) => {
            setIntakeReviewId(null);
            openProfileTab(patientId, { label: displayName, section: "overview" });
            void navigate({
              to: "/patients/$patientId",
              params: { patientId },
              search: { section: "overview" },
            });
          }}
        />
      ) : null}
    </main>
  );
}
